import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";

const CATEGORIES = ["All", "Social Media", "Web", "Mobile", "Campaign", "PR", "Video"];

const Portfolio = () => {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/portfolio").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [active]);

  const filtered = filter === "All" ? items : items.filter((p) => p.category === filter);

  return (
    <div data-testid="portfolio-page">
      <section className="section-pad" style={{ paddingTop: "clamp(160px, 18vw, 220px)" }}>
        <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 32 }}>
          {t.portfolio.label}
        </div>
        <h1 className="font-display" style={{ fontSize: "clamp(3rem, 8vw, 7rem)", fontWeight: 300, lineHeight: 1, color: "var(--warm-off-white)", letterSpacing: "-0.02em" }}>
          {t.portfolio.title}
        </h1>
        <p className="font-body" style={{ color: "rgba(248,244,240,0.65)", lineHeight: 1.7, marginTop: 24, fontSize: "1.1rem", maxWidth: 640 }}>
          {t.portfolio.intro}
        </p>
      </section>

      <section className="section-pad" style={{ paddingTop: 0 }}>
        <div className="flex flex-wrap" style={{ gap: 8, marginBottom: 48, borderBottom: "1px solid rgba(248,244,240,0.08)", paddingBottom: 16 }}>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setFilter(c)}
              className="font-mono"
              style={{
                background: "transparent", border: "1px solid", borderColor: filter === c ? "var(--gold)" : "rgba(248,244,240,0.15)",
                color: filter === c ? "var(--gold)" : "rgba(248,244,240,0.7)",
                padding: "10px 20px", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: "0.7rem", cursor: "pointer",
                transition: "all 0.4s var(--ease-cinema)",
              }}
            >
              {c === "All" ? t.portfolio.all : c}
            </button>
          ))}
        </div>

        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 24, gridAutoRows: "auto" }}>
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 60}>
              <div
                data-testid={`portfolio-item-${p.id}`}
                onClick={() => setActive(p)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") setActive(p); }}
                style={{ cursor: "pointer", position: "relative", overflow: "hidden", background: "var(--rich-black-light)", aspectRatio: i % 5 === 0 ? "3/4" : "4/5" }}
              >
                <img src={p.image_url} alt={lang === "tr" ? p.title_tr : p.title_en} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.8s var(--ease-cinema)", filter: "brightness(0.8)" }} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 30%, rgba(10,10,10,0.95) 100%)" }} />
                <div style={{ position: "absolute", bottom: 24, left: 24, right: 24 }}>
                  <span className="font-mono" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase" }}>{p.category} — {p.year}</span>
                  <h3 className="font-display" style={{ fontSize: "1.5rem", fontWeight: 400, color: "var(--warm-off-white)", marginTop: 8, lineHeight: 1.15 }}>{lang === "tr" ? p.title_tr : p.title_en}</h3>
                  <div className="font-mono" style={{ fontSize: "0.8rem", color: "var(--gold-light)", marginTop: 8 }}>{p.result_metric}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {active && (
        <div data-testid="portfolio-modal" onClick={() => setActive(null)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(10,10,10,0.94)", backdropFilter: "blur(14px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--rich-black-light)", border: "1px solid rgba(201,151,58,0.35)", maxWidth: 1000, width: "100%", maxHeight: "90vh", overflowY: "auto", position: "relative" }}>
            <button data-testid="portfolio-modal-close" onClick={() => setActive(null)} style={{ position: "absolute", top: 16, right: 16, background: "rgba(10,10,10,0.7)", border: "none", color: "var(--warm-off-white)", padding: 10, zIndex: 5 }}>
              <X size={22} />
            </button>
            <div style={{ aspectRatio: "16/9", overflow: "hidden" }}>
              <img src={active.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div style={{ padding: "40px clamp(24px, 4vw, 56px)" }}>
              <span className="font-mono" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase" }}>{active.category} — {active.year}</span>
              <h2 className="font-display" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)", fontWeight: 300, color: "var(--warm-off-white)", lineHeight: 1.05, marginTop: 12 }}>
                {lang === "tr" ? active.title_tr : active.title_en}
              </h2>
              <p style={{ color: "rgba(248,244,240,0.78)", lineHeight: 1.8, marginTop: 24, fontSize: "1.05rem" }}>
                {lang === "tr" ? active.description_tr : active.description_en}
              </p>
              <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 24, marginTop: 40, paddingTop: 32, borderTop: "1px solid rgba(248,244,240,0.08)" }}>
                <div>
                  <div className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase" }}>{t.portfolio.client}</div>
                  <div style={{ color: "var(--warm-off-white)", marginTop: 8 }}>{active.client || "—"}</div>
                </div>
                <div>
                  <div className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase" }}>{t.portfolio.category}</div>
                  <div style={{ color: "var(--warm-off-white)", marginTop: 8 }}>{active.category}</div>
                </div>
                <div>
                  <div className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase" }}>{t.portfolio.year}</div>
                  <div style={{ color: "var(--warm-off-white)", marginTop: 8 }}>{active.year}</div>
                </div>
                <div>
                  <div className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase" }}>{t.portfolio.result}</div>
                  <div style={{ color: "var(--gold-light)", marginTop: 8 }} className="font-mono">{active.result_metric}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
